import { useEffect, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { ReviewList } from "@/components/reviews/ReviewList";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Img } from "@/components/ui/img";
import { Dialog } from "@/components/ui/dialog";
import {
  CaretRight,
  CircleNotch,
  Users,
  Check,
  CaretLeft,
  Monitor,
  Translate,
  Brain,
  DotsThree,
  type Icon,
} from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { BookingCalendar, BookingClosedNotice } from "@/components/booking";
import { TherapistArticles } from "@/components/blog/TherapistArticles";
import { TherapistIntroVideo, resolveIntroVideo } from "@/components/therapist/profile/TherapistIntroVideo";
import { getTherapistBookingAvailability } from "@/lib/therapist-booking";
import { PROBLEM_TAG_LABELS, isProblemTag } from "@/lib/matching/taxonomy";
import type { TherapistDetail } from "@/types";

export const Route = createFileRoute("/therapists/$therapistId")({
  component: TherapistProfilePage,
});

function TherapistProfilePage() {
  const { t } = useTranslation();
  const { therapistId } = Route.useParams();
  const search = useSearch({ strict: false }) as { review?: string; book?: string; match?: string };
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [eligibleForReview, setEligibleForReview] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(search.review === "1");

  useEffect(() => {
    if (search.book !== "1") return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 767px)").matches) {
      setShowBookingDialog(true);
    }
  }, [search.book]);

  // therapistId is the route param — a slug on the canonical URL, and still
  // sometimes the old id via a shared/bookmarked link. Every id-keyed
  // sub-request below (reviewable check, services, reviews, review form)
  // must key off the fetched row's real `id`, never off this param, or a
  // slug never matches an id and those requests silently come back empty.
  const { data: therapist, isLoading, error } = useQuery({
    queryKey: ["therapists", therapistId],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${therapistId}`);
      if (!res.ok) throw new Error("Therapist not found");
      return res.json() as Promise<TherapistDetail>;
    },
  });

  useEffect(() => {
    if (!therapist?.id) return;
    fetch("/api/me/reviewable-therapists")
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((b: { items: Array<{ therapistId: string }> }) => {
        setEligibleForReview(b.items.some((x) => x.therapistId === therapist.id));
      })
      .catch(() => setEligibleForReview(false));
  }, [therapist?.id]);

  const { data: servicesEnv } = useQuery({
    queryKey: ["services", "therapist", therapist?.id],
    queryFn: async () => {
      const res = await fetch(`/api/services?therapistId=${therapist!.id}`);
      if (!res.ok) throw new Error("Failed to fetch services");
      return res.json() as Promise<{
        services: NonNullable<TherapistDetail["services"]>;
        resolvedCountry: string | null;
      }>;
    },
    enabled: !!therapist?.id,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircleNotch className="size-8 animate-spin text-primary/40" />
      </div>
    );
  }

  if (error || !therapist) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-muted/40">
          <Users className="size-7 text-muted-foreground" />
        </div>
        <h2
          className="mt-4 text-2xl font-bold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("therapistDetail.notFound")}
        </h2>
        <p className="mt-2 text-muted-foreground">{t("therapistDetail.notFoundDesc")}</p>
        <Link
          to="/therapists"
          className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          <CaretLeft className="size-4" />
          {t("therapistDetail.backToList")}
        </Link>
      </div>
    );
  }

  const topics = topicLabels(therapist.problemTags);
  const initial = therapist.userName?.charAt(0)?.toUpperCase() || "?";
  const bookingServices = servicesEnv?.services ?? therapist.services ?? [];
  const bookingAvailability = getTherapistBookingAvailability({
    services: bookingServices,
  });
  // Informational badge only; it never gates the calendar.
  const acceptingNew = therapist.acceptingNewClients;

  const sortedLanguages = Array.isArray(therapist.languages)
    ? [...therapist.languages].sort((a, b) =>
        t(`languages.names.${a}`).localeCompare(t(`languages.names.${b}`)),
      )
    : [];

  return (
    <div
      className="min-h-screen pb-24 lg:pb-0"
      style={{ background: "oklch(98.5% 0.005 84)" }}
    >
      {/* Breadcrumb */}
      <div
        style={{
          background: "oklch(97% 0.01 84)",
          borderBottom: "1px solid oklch(92% 0.02 84)",
        }}
      >
        <div className="mx-auto max-w-6xl px-4 py-3 sm:px-6">
          <nav className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground sm:text-xs">
            <Link to="/" className="hover:text-foreground">
              {t("therapistDetail.breadcrumbHome")}
            </Link>
            <CaretRight className="size-3" />
            <Link to="/therapists" className="hover:text-foreground">
              {t("therapistDetail.breadcrumbTherapists")}
            </Link>
            <CaretRight className="size-3" />
            <span className="truncate text-foreground">{therapist.userName}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 pt-6 pb-12 sm:px-6 sm:pt-10 sm:pb-16 lg:pt-12 lg:pb-24">
        {/* ============== HERO ============== */}
        <section>
          {/* MOBILE hero (< sm): compact horizontal header */}
          <div className="flex items-start gap-4 sm:hidden">
            <div className="relative shrink-0">
              <div className="size-24 overflow-hidden rounded-2xl">
                {therapist.profileImageUrl ? (
                  <Img
                    src={therapist.profileImageUrl}
                    alt={therapist.userName}
                    sizes="96px"
                    className="size-full object-cover"
                  />
                ) : (
                  <PhotoFallback initial={initial} className="text-4xl" />
                )}
              </div>
              {acceptingNew && (
                <span
                  className="absolute -bottom-1.5 -right-1.5 grid size-6 place-items-center rounded-full bg-emerald-500"
                  style={{ boxShadow: "0 0 0 2px oklch(98.5% 0.005 84)" }}
                  title={t("therapistDetail.acceptingNew")}
                >
                  <Check className="size-3.5 text-white" weight="bold" />
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-700">
                {t("therapistDetail.eyebrow")}
              </div>
              <h1
                className="mt-1 text-3xl font-medium leading-[1.05] tracking-tight text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {therapist.userName}
              </h1>
              {acceptingNew && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-emerald-700">
                  <span className="size-1.5 rounded-full bg-emerald-500" />
                  {t("therapistDetail.acceptingNew")}
                </div>
              )}
            </div>
          </div>

          {/* TABLET + DESKTOP hero (sm+): compact portrait on the left */}
          <div className="hidden gap-8 sm:grid sm:grid-cols-[minmax(170px,230px)_1fr] sm:items-center lg:gap-12">
            <div className="relative">
              <div className="aspect-[4/5] w-full overflow-hidden rounded-sm">
                {therapist.profileImageUrl ? (
                  <Img
                    src={therapist.profileImageUrl}
                    alt={therapist.userName}
                    sizes="230px"
                    className="size-full object-cover"
                  />
                ) : (
                  <PhotoFallback initial={initial} className="text-[5rem] lg:text-[6rem]" />
                )}
              </div>
              {acceptingNew && (
                <div className="absolute -bottom-3 -left-2 -rotate-3 bg-card px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider shadow-sm ring-1 ring-black/5 sm:px-3 sm:py-1.5">
                  {t("therapistDetail.acceptingNew")}
                </div>
              )}
            </div>

            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-amber-700">
                {t("therapistDetail.eyebrow")}
              </div>
              <h1
                className="mt-3 text-5xl font-medium leading-[1.05] tracking-tight text-foreground lg:text-6xl"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {therapist.userName}
              </h1>
              {therapist.isVolunteer && (
                <div className="mt-4">
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800">
                    Volonter
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Stats row — all sizes */}
          <div className="mt-6 flex flex-col gap-5 border-y border-neutral-300/70 py-5 text-sm sm:mt-10 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-12">
            <Stat
              icon={Monitor}
              label={t("therapistDetail.modeShort", { defaultValue: "Način rada" })}
              items={[
                t(
                  `therapist.profile.modality${
                    therapist.sessionModality === "in_person"
                      ? "InPerson"
                      : therapist.sessionModality === "both"
                        ? "Both"
                        : "Online"
                  }`,
                ),
              ]}
            />
            {sortedLanguages.length > 0 && (
              <Stat
                icon={Translate}
                label={t("therapistDetail.languagesShort")}
                items={sortedLanguages.map((code) => t(`languages.names.${code}`))}
              />
            )}
            {topics.length > 0 && (
              <Stat
                icon={Brain}
                label={t("therapistDetail.topicsTitle")}
                items={topics}
              />
            )}
          </div>
        </section>

        {/* ============== BOOKING (md+) — full-width split layout ============== */}
        <section className="mt-10 hidden md:block md:mt-12 lg:mt-14">
          {!bookingAvailability.canBook ? (
            <BookingClosedNotice />
          ) : (
            <BookingCalendar
              therapistId={therapist.id}
              therapistName={therapist.userName}
              services={bookingServices}
              resolvedCountry={servicesEnv?.resolvedCountry ?? null}
              officeAddress={therapist.officeAddress ?? null}
              matchingResultToken={search.match}
            />
          )}
        </section>

        {/* ============== BODY ============== */}
        <div className="mt-10 space-y-10 sm:mt-14 lg:space-y-12">
          {/* About */}
            {therapist.bio && (
              <section>
                <h2
                  className="text-2xl font-medium text-foreground sm:text-3xl"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {t("therapistDetail.aboutTherapist")}
                </h2>
                <div className="mt-3 whitespace-pre-line text-base leading-relaxed text-neutral-800 sm:mt-4 sm:text-lg">
                  <p style={{ fontFamily: "var(--font-heading)" }}>{therapist.bio}</p>
                </div>
              </section>
            )}

            {/* Topics */}
            {topics.length > 0 && (
              <section>
                <h2
                  className="text-2xl font-medium text-foreground sm:text-3xl"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {t("therapistDetail.topicsTitle")}
                </h2>
                <ul className="mt-3 divide-y divide-neutral-200 border-y border-neutral-200 sm:mt-4">
                  {topics.map((topic, idx) => (
                    <li
                      key={topic}
                      className="flex items-baseline justify-between py-3 text-sm sm:text-base"
                    >
                      <span className="font-medium text-foreground">{topic}</span>
                      <span className="font-mono text-[10px] text-muted-foreground sm:text-xs">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Intro video */}
            {resolveIntroVideo(therapist.profileVideoUrl) && (
              <section>
                <h2
                  className="text-2xl font-medium text-foreground sm:text-3xl"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {t("therapistDetail.videoIntro")}
                </h2>
                <div className="mt-3 sm:mt-4">
                  <TherapistIntroVideo url={therapist.profileVideoUrl!} title={t("therapistDetail.videoIntro")} />
                </div>
              </section>
            )}

            {/* Articles */}
            <TherapistArticles therapistId={therapist.id} />

            {/* Reviews */}
            <section className="space-y-4">
              <h2
                className="text-2xl font-medium text-foreground sm:text-3xl"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {t("therapistDetail.reviewsTitle")}
              </h2>
              <ReviewList therapistId={therapist.id} />
              {eligibleForReview && !reviewOpen && (
                <Button onClick={() => setReviewOpen(true)} size="sm">
                  {t("therapistDetail.leaveReview")}
                </Button>
              )}
              {reviewOpen && (
                <div className="rounded-lg border border-neutral-200 p-4">
                  <ReviewForm
                    therapistId={therapist.id}
                    onSuccess={() => {
                      setReviewOpen(false);
                      window.location.reload();
                    }}
                  />
                </div>
              )}
            </section>
        </div>
      </div>

      {/* Mobile/tablet sticky booking bar */}
      {bookingAvailability.canBook && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 p-3 md:hidden"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div
            className="mx-auto flex max-w-6xl items-center gap-3 rounded-2xl border bg-card px-4 py-3 shadow-[0_10px_34px_-8px_rgba(35,25,10,0.32)]"
            style={{ borderColor: "oklch(86% 0.03 84)" }}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {acceptingNew && (
                  <span className="size-1.5 rounded-full bg-emerald-500" />
                )}
                {acceptingNew
                  ? t("therapistDetail.acceptingNew")
                  : t("therapistDetail.eyebrow")}
              </div>
              <div className="truncate text-sm font-semibold text-foreground">
                {therapist.userName}
              </div>
            </div>
            <Button
              size="lg"
              className="h-12 shrink-0 gap-1.5 rounded-xl px-6 text-[15px] font-semibold shadow-lg shadow-primary/25 transition-transform active:scale-[0.98]"
              onClick={() => setShowBookingDialog(true)}
            >
              {t("therapistDetail.bookAppointment")}
              <CaretRight className="size-4" weight="bold" />
            </Button>
          </div>
        </div>
      )}
      {!bookingAvailability.canBook && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 p-3 md:hidden"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div
            className="mx-auto max-w-6xl rounded-2xl border bg-card px-4 py-3 shadow-[0_10px_34px_-8px_rgba(35,25,10,0.32)]"
            style={{ borderColor: "oklch(86% 0.03 84)" }}
          >
            <BookingClosedNotice />
          </div>
        </div>
      )}

      {/* Mobile booking dialog */}
      {showBookingDialog && bookingAvailability.canBook && (
        <Dialog
          open
          onClose={() => setShowBookingDialog(false)}
          className="h-[100dvh] sm:h-auto sm:max-w-lg"
        >
          <div className="flex-1 overflow-y-auto">
            <BookingCalendar
              therapistId={therapist.id}
              therapistName={therapist.userName}
              services={bookingServices}
              resolvedCountry={servicesEnv?.resolvedCountry ?? null}
              officeAddress={therapist.officeAddress ?? null}
              matchingResultToken={search.match}
              onClose={() => setShowBookingDialog(false)}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
}

function Stat({ icon: IconCmp, label, items }: { icon: Icon; label: string; items: string[] }) {
  const visible = items.slice(0, 2);
  const hidden = items.slice(2);
  return (
    <div className="flex items-start gap-2.5">
      <IconCmp
        className="mt-px size-[18px] shrink-0 text-amber-700"
        weight="light"
        aria-hidden
      />
      <div className="min-w-0">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground sm:text-xs">
          {label}
        </span>
        <div className="flex items-center gap-1.5 font-medium text-foreground">
          <span>
            {visible.join(", ")}
            {hidden.length > 0 ? " …" : ""}
          </span>
          {hidden.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`${hidden.length} ${label}`}
                  className="inline-grid size-5 shrink-0 place-items-center rounded-full text-amber-800 transition-colors hover:bg-amber-100"
                  style={{ background: "oklch(37.12% 0.059 186.62 / 0.1)" }}
                >
                  <DotsThree className="size-3.5" weight="bold" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <ul className="space-y-0.5">
                  {hidden.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoFallback({ initial, className = "" }: { initial: string; className?: string }) {
  return (
    <div
      className="flex size-full items-center justify-center"
      style={{
        background: "linear-gradient(135deg, oklch(90% 0.06 84), oklch(78% 0.13 60))",
      }}
    >
      <span
        className={`font-medium text-white/85 ${className}`}
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {initial}
      </span>
    </div>
  );
}

function topicLabels(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  return tags.filter(isProblemTag).map((tag) => PROBLEM_TAG_LABELS[tag]);
}
